import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { flushSync } from 'react-dom'
import * as FaIcons from 'react-icons/fa'
import * as FiIcons from 'react-icons/fi'
import { BusinessSidebarData, SidebarData } from './SidebarData/SidebarData'
import { SubMenu } from './SubMenu/SubMenu'
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { COLORS } from '@utils/colors'
import { PrimaryText } from '@components/Common/PrimaryText/PrimaryText'
import { useAuth } from '@context/AuthContext/AuthContext'
import { logout } from '@services/AuthService'
import { useDisplay } from '@context/DisplayContext/DisplayContext'
import { getOrg } from '@utils/helpers'
import { getUsersName } from '@utils/strings'
import { useOrganization } from '@context/OrganizationContext/OrganizationContext'
import { ROLES } from '@utils/appData/appData'
import type { TipsConfig } from '@Interfaces/Constants/DataInterface'
import {
  filterVisibleSidebarNavItems,
  getVisibleSidebarTipSteps,
  resolveTipsConfigForOrg,
  resolveTourNavigateTarget,
  SIDEBAR_TIP_STEP,
} from '@utils/sidebarTipSteps'
import { CustomPicker } from '@components/Inputs/CustomPicker/CustomPicker'
import SidebarTips from '@components/Modals/SidebarTips/SidebarTips'
import Welcome from '@components/Modals/Welcome/Welcome'
import { Links } from '@utils/links'
import { useLocation, useNavigate } from 'react-router'
import { Box, ClickAwayListener, Drawer, IconButton } from '@mui/material'
import useMediaQuery from '@hooks/useMediaQuery'
import { LuMenu } from 'react-icons/lu'
import rayzeLogo from '@assets/rayzeLogo.png'
import { User } from '@Interfaces/Data/UserInterface'
import { isBusinessOrganization } from '@utils/orgTypeHelpers'

type SidebarProps = Record<string, never>
type TipFlow = 'idle' | 'welcome' | 'touring'

const Sidebar: React.FC<SidebarProps> = React.memo(() => {
  const { currentUser } = useAuth()
  const {
    selectedOrg,
    setSelectedOrg,
    ourOrgOptions,
    userType,
    currentMember,
    pendingChanges,
    loading,
    setExitModal: setExitModalOpen,
  } = useOrganization()

  const { showSidebar, setShowSidebar } = useDisplay()
  const navigate = useNavigate()
  const location = useLocation()

  const runTourNavigate = useCallback(
    (to: string) => {
      flushSync(() => {
        navigate(to)
      })
    },
    [navigate]
  )

  const { getResponsiveValue } = useMediaQuery()
  const [isShowSidebar, setIsShowSidebar] = useState(false)
  const [subnav, setSubnav] = useState<string | null>(null)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [tipFlow, setTipFlow] = useState<TipFlow>('idle')
  const [tourIndex, setTourIndex] = useState(0)
  const [hideTips, setHideTips] = useState(true)

  const logoutPressed = useCallback(() => {
    logout()
    window.location.reload()
  }, [])

  const handleProfileMenuToggle = useCallback(() => {
    setShowProfileMenu(prev => !prev)
  }, [])

  const nav = useMemo(
    () => (isBusinessOrganization(selectedOrg) ? BusinessSidebarData : SidebarData),
    [selectedOrg?.type]
  )

  const visibleNav = useMemo(
    () =>
      selectedOrg ? filterVisibleSidebarNavItems(nav, selectedOrg, userType) : [],
    [nav, selectedOrg, userType]
  )

  const tipsConfig: Partial<TipsConfig> = useMemo(
    () => resolveTipsConfigForOrg(selectedOrg),
    [selectedOrg]
  )

  const tipStepPaths = useMemo(
    () =>
      selectedOrg ? getVisibleSidebarTipSteps(visibleNav, tipsConfig) : [],
    [selectedOrg, visibleNav, tipsConfig]
  )

  const didJumpDashboardForTourRef = useRef(false)

  const resolveNextPathOverride = useCallback(
    (stepKey: string): string | null | undefined => {
      const idx = tipStepPaths.indexOf(stepKey)
      if (idx === -1) return undefined
      if (idx < tipStepPaths.length - 1) return tipStepPaths[idx + 1]!
      const tipEntry = tipsConfig[stepKey]
      const next = tipEntry?.nextPath
      if (
        next &&
        (tipEntry?.buttonTitle === 'Finish' || stepKey === SIDEBAR_TIP_STEP)
      ) {
        return next
      }
      return null
    },
    [tipStepPaths, tipsConfig]
  )

  useEffect(() => {
    setTourIndex(0)
    didJumpDashboardForTourRef.current = false
  }, [selectedOrg?.id])

  useEffect(() => {
    if (!selectedOrg || !currentMember) {
      setTipFlow('idle')
      return
    }
    if (currentMember.hideTips) {
      setTipFlow('idle')
      setHideTips(true)
      return
    }
    if (!tipsConfig.sidebar) {
      setTipFlow('idle')
      return
    }
    setHideTips(false)
    setTourIndex(0)
    setTipFlow(currentMember.hideTips === undefined ? 'welcome' : 'touring')
  }, [selectedOrg?.id, currentMember, currentMember?.hideTips, tipsConfig.sidebar])

  useEffect(() => {
    if (tipFlow !== 'touring') return
    if (currentMember?.hideTips) return
    if (!tipsConfig.sidebar) return
    if (didJumpDashboardForTourRef.current) return
    didJumpDashboardForTourRef.current = true
    const home = isBusinessOrganization(selectedOrg)
      ? Links.BusinessDashboard
      : Links.Dashboard
    runTourNavigate(home)
  }, [
    tipFlow,
    currentMember?.hideTips,
    tipsConfig.sidebar,
    selectedOrg,
    runTourNavigate,
  ])

  useEffect(() => {
    if (loading) return
    if (selectedOrg?.type === 'business' && userType !== 'Leader') {
      const restrictedPaths: string[] = [
        Links.BusinessEmployees,
        Links.BusinessEmployeeDetail,
        Links.GeneralSettings,
      ]

      if (restrictedPaths.includes(location.pathname)) {
        navigate(Links.BusinessDashboard)
      }
    }
  }, [selectedOrg?.type, userType, location.pathname, navigate, loading])

  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        px={12}
        height={80}
        justifyContent={{ xs: 'space-between', lg: 'flex-end' }}
        alignItems="center"
        ml={{ xs: 0, lg: showSidebar ? 280 : 60 }}
      >
        <IconButton
          sx={{ display: { xs: 'block', lg: 'none' } }}
          onClick={() => setIsShowSidebar(true)}
        >
          <LuMenu size={24} />
        </IconButton>

        <TouchableOpacity
          style={styles.userContainer}
          onPress={handleProfileMenuToggle}
        >
          <View style={styles.userIcon}>
            {currentUser?.profilePic ? (
              <Image source={{ uri: currentUser.profilePic }} style={styles.profilePic} />
            ) : (
              <FaIcons.FaUser size={20} color={COLORS.primary.white} />
            )}
          </View>
          <View style={{ flex: 1, marginHorizontal: 24 }}>
            <PrimaryText fontSize={20} fontWeight={900} style={styles.userName} numberOfLines={1}>
              {currentUser ? getUsersName(currentUser as User) : ''}
            </PrimaryText>
            {!!userType && (
              <PrimaryText fontSize={12} fontWeight={400} style={styles.status} numberOfLines={1}>
                {ROLES.find((x) => x.value === userType)?.label}
              </PrimaryText>
            )}
          </View>
          <FiIcons.FiChevronDown size={24} color={COLORS.primary.darkGray} />
        </TouchableOpacity>
      </Box>

      <Drawer
        open={getResponsiveValue({ xs: isShowSidebar, lg: true }) as boolean}
        onClose={() => setIsShowSidebar(false)}
        variant={
          getResponsiveValue({ xs: 'temporary', lg: 'persistent' }) as
            | 'temporary'
            | 'persistent'
        }
      >
        <View
          style={{
            width: showSidebar ? '280px' : '60px',
            justifyContent: 'space-between',
            paddingBottom: 12,
            height: '100vh',
            backgroundColor: COLORS.primary.offWhite,
          }}
        >
          <View>
            <View style={[styles.companyInfoContainer, showSidebar && styles.companyInfoContainerExpanded]}>
              <View style={[styles.rayzeContainer, !showSidebar && { justifyContent: 'center' }]}>
                <Image
                  source={rayzeLogo}
                  style={[
                    styles.rayzeLogo,
                    showSidebar && styles.rayzeLogoExpanded,
                    !showSidebar && {
                      marginBottom: 0,
                      height: 30,
                      width: 60,
                    },
                  ]}
                />

                {showSidebar && (
                  <>
                    <View style={styles.sidebarDivider} />
                    <View style={styles.myOrgHeader}>
                      <PrimaryText
                        fontSize={16}
                        fontWeight={800}
                        style={styles.myOrgTitle}
                        numberOfLines={1}
                      >
                        My Organizations
                      </PrimaryText>
                    </View>
                  </>
                )}
              </View>

              {showSidebar && (
                <View style={styles.pickerWrapper}>
                  <SidebarTips
                    path="sidebar"
                    index={0}
                    totalSteps={tipStepPaths.length}
                    show={
                      tipFlow === 'touring' &&
                      tourIndex === 0 &&
                      tipStepPaths[0] === SIDEBAR_TIP_STEP
                    }
                    isLastVisibleStep={false}
                    onNext={() => {
                      const tipEntry = tipsConfig[SIDEBAR_TIP_STEP]
                      const finishLike = tipEntry?.buttonTitle === 'Finish'
                      const override = resolveNextPathOverride(SIDEBAR_TIP_STEP)
                      const target = resolveTourNavigateTarget(
                        override,
                        finishLike,
                        tipEntry?.nextPath
                      )
                      if (target) runTourNavigate(target)
                      setTourIndex(1)
                    }}
                  >
                    <CustomPicker
                      value={selectedOrg ? getOrg(ourOrgOptions, selectedOrg)?.value : undefined}
                      onChange={(value) => {
                        const org = ourOrgOptions?.find((x) => x.value === value)?.org
                        if (org) {
                          setSelectedOrg?.(org)
                        }
                      }}
                      options={ourOrgOptions || []}
                      label={selectedOrg?.name}
                    />
                  </SidebarTips>
                </View>
              )}
            </View>

            <View style={styles.navList}>
              {visibleNav.map((item, index) => {
                const stepIndex = item.path ? tipStepPaths.indexOf(item.path) : -1

                return (
                  <SidebarTips
                    key={'sidebar-tips-' + (item.path ?? index)}
                    index={stepIndex >= 0 ? stepIndex : 0}
                    totalSteps={tipStepPaths.length}
                    path={item.path}
                    show={
                      tipFlow === 'touring' &&
                      !!item.path &&
                      tipStepPaths[tourIndex] === item.path
                    }
                    isLastVisibleStep={stepIndex === tipStepPaths.length - 1}
                    onNext={() => {
                      if (!item.path || stepIndex < 0) return
                      const tipEntry = tipsConfig[item.path]
                      const finishLike =
                        tipEntry?.buttonTitle === 'Finish' ||
                        stepIndex === tipStepPaths.length - 1
                      const override = resolveNextPathOverride(item.path)
                      const target = resolveTourNavigateTarget(
                        override,
                        finishLike,
                        tipEntry?.nextPath
                      )
                      if (target) runTourNavigate(target)
                      setTourIndex(stepIndex + 1)
                    }}
                  >
                    <SubMenu
                      onClick={() => setIsShowSidebar(false)}
                      subnav={subnav}
                      setSubnav={setSubnav}
                      item={item}
                      currentUser={currentUser as User}
                      showSidebar={showSidebar}
                      setShowSidebar={setShowSidebar}
                      hideBusinessEmployeeNav={
                        selectedOrg?.type === 'business' && userType !== 'Leader'
                      }
                      pendingChanges={pendingChanges}
                      setExitModal={(_path: string) => setExitModalOpen(true)}
                    />
                  </SidebarTips>
                )
              })}
            </View>
          </View>

          <TouchableOpacity
            style={{
              alignSelf: showSidebar ? 'flex-end' : 'center',
              marginRight: showSidebar ? 12 : 0,
            }}
            onPress={() => setShowSidebar(!showSidebar)}
          >
            {showSidebar ? (
              <FaIcons.FaChevronLeft size={20} color={COLORS.primary.darkGray} />
            ) : (
              <FaIcons.FaChevronRight size={20} color={COLORS.primary.darkGray} />
            )}
          </TouchableOpacity>
        </View>
      </Drawer>

      {showProfileMenu && (
        <ClickAwayListener onClickAway={() => setShowProfileMenu(false)}>
          <View style={styles.profileMenu}>
            <TouchableOpacity style={styles.menuButton} onPress={logoutPressed}>
              <PrimaryText fontSize={12} fontWeight={300} style={styles.userName} numberOfLines={1}>
                Logout
              </PrimaryText>
              <FaIcons.FaSignOutAlt size={20} color={COLORS.primary.darkGray} />
            </TouchableOpacity>
          </View>
        </ClickAwayListener>
      )}

      {tipFlow === 'welcome' && !hideTips && (
        <Welcome
          onPress={() => {
            setTipFlow('touring')
            setTourIndex(0)
          }}
        />
      )}
    </>
  )
})

Sidebar.displayName = 'Sidebar'

const styles = StyleSheet.create({
  companyInfoContainer: {
    marginVertical: 12,
    zIndex: 500,
  },
  companyInfoContainerExpanded: {
    marginVertical: 0,
  },
  rayzeContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  rayzeLogo: {
    height: 92,
    width: 132,
    resizeMode: 'contain',
    marginVertical: 30,
  },
  rayzeLogoExpanded: {
    marginTop: 0,
    marginBottom: 44,
  },
  sidebarDivider: {
    width: '100%',
    height: 1,
    backgroundColor: COLORS.primary.lightGray,
  },
  myOrgHeader: {
    width: '100%',
    paddingTop: 27,
    paddingBottom: 10,
    paddingHorizontal: 15,
  },
  pickerWrapper: {
    marginHorizontal: 15,
    marginTop: 0,
    marginBottom: 16,
  },
  navList: {
    gap: 2,
  },
  companyName: {
    fontSize: 16,
    color: COLORS.primary.black,
    marginVertical: 12,
    textAlign: 'center',
  },
  showSidebarButton: {
    alignSelf: 'center',
  },
  topBar: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    backgroundColor: COLORS.primary.offWhite,
    height: 80,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  notificationContainer: {
    backgroundColor: COLORS.primary.green,
    height: 36,
    width: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  userContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  userIcon: {
    backgroundColor: COLORS.primary.darkGray,
    height: 36,
    width: 36,
    borderRadius: 18,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
  },
  profilePic: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  userName: {
    fontSize: 14,
    lineHeight: 24,
    color: COLORS.primary.darkGray,
  },
  status: {
    fontSize: 12,
    color: COLORS.primary.lightBlue,
  },
  myOrgTitle: {
    fontSize: 16,
    lineHeight: 24,
    color: COLORS.primary.darkBlue,
    textAlign: 'left',
    width: '100%',
  },
  profileMenu: {
    position: 'absolute',
    zIndex: 1,
    backgroundColor: COLORS.primary.white,
    right: 0,
    top: 80,
    height: 40,
    width: 200,
    paddingHorizontal: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 3.5,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  menuButton: {
    flexDirection: 'row',
    width: '100%',
    height: 40,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
})

export default Sidebar
